import { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, GitCompareArrows } from 'lucide-react';
import type { VerbatimRespondent } from '../../data/verbatimData';
import { InfoButton } from '../common/Tooltip';

const FEATURES = [
  'Smart Biometrics Ring',
  'Workout Sessions',
  'Body Composition Analysis',
  'Recovery Sessions',
  'DNA Health Report',
  'Personalized Wellness Plan',
  'Nutrition Tracking',
];

interface FeatureRow {
  name: string;
  avgRank: number;
  statedImportancePct: number;
  revealedPrefPct: number;
  gap: number;
}

function computeRows(respondents: VerbatimRespondent[]): FeatureRow[] {
  const n = respondents.length;
  if (n === 0) return [];

  return FEATURES.map(name => {
    let rankSum = 0;
    let rankCount = 0;
    let importantCount = 0;
    let revealedSum = 0;
    let revealedCount = 0;

    for (const r of respondents) {
      const rank = r.featureRanking[name];
      if (rank > 0) { rankSum += rank; rankCount++; }

      const imp = r.featureImportance[name];
      if (imp === 'Very important' || imp === 'Extremely important') importantCount++;

      const rate = r.featureChoiceRates[name];
      if (rate !== undefined) { revealedSum += rate; revealedCount++; }
    }

    const avgRank = rankCount > 0 ? +(rankSum / rankCount).toFixed(1) : 0;
    const statedImportancePct = Math.round(importantCount / n * 100);
    const revealedPrefPct = revealedCount > 0 ? Math.round(revealedSum / revealedCount * 100) : 0;
    const gap = revealedPrefPct - statedImportancePct;

    return { name, avgRank, statedImportancePct, revealedPrefPct, gap };
  }).sort((a, b) => a.avgRank - b.avgRank);
}

interface Props {
  respondents: VerbatimRespondent[];
}

export function SaidVsDidPanel({ respondents }: Props) {
  const [expanded, setExpanded] = useState(true);
  const rows = useMemo(() => computeRows(respondents), [respondents]);

  if (rows.length === 0) return null;

  return (
    <div className="card mb-4">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center justify-between w-full text-left"
      >
        <div className="flex items-center gap-2">
          <GitCompareArrows size={14} className="text-chart-teal" />
          <h3 className="text-heading font-semibold text-secondary uppercase tracking-wider">
            Said vs. Did — Feature Preferences
          </h3>
          <InfoButton helpKey="saidVsDid" size={13} />
        </div>
        {expanded ? <ChevronUp size={14} className="text-muted" /> : <ChevronDown size={14} className="text-muted" />}
      </button>

      {expanded && (
        <>
          <p className="text-detail text-muted mt-2 mb-3">
            Comparing what respondents said matters (survey Q5/Q6) to what they actually chose in the conjoint exercise.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border text-muted">
                  <th className="text-left py-2 pr-3 font-medium">Feature</th>
                  <th className="text-right py-2 px-3 font-medium">Stated Rank</th>
                  <th className="text-right py-2 px-3 font-medium">Said Important</th>
                  <th className="text-right py-2 px-3 font-medium">Actually Chose</th>
                  <th className="text-right py-2 pl-3 font-medium">Gap</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => {
                  const absGap = Math.abs(row.gap);
                  const gapColor = absGap > 15
                    ? row.gap > 0 ? 'text-metric-positive' : 'text-metric-negative'
                    : 'text-muted';
                  return (
                    <tr key={row.name} className="border-b border-border/50">
                      <td className="py-2 pr-3 font-medium text-primary">{row.name}</td>
                      <td className="py-2 px-3 text-right font-mono text-secondary tabular-nums">#{row.avgRank}</td>
                      <td className="py-2 px-3 text-right font-mono text-secondary tabular-nums">{row.statedImportancePct}%</td>
                      <td className="py-2 px-3 text-right font-mono text-secondary tabular-nums">{row.revealedPrefPct}%</td>
                      <td className={`py-2 pl-3 text-right font-mono font-semibold tabular-nums ${gapColor}`}>
                        {row.gap > 0 ? '+' : ''}{row.gap}pp
                        {absGap > 15 && <span className="ml-1 text-micro font-sans">{row.gap > 0 ? '(Hidden Gem)' : '(Overhyped)'}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-micro text-muted mt-3 italic">
            "Said Important" = % who rated the feature "Very" or "Extremely" important (Q5).
            "Actually Chose" = avg % of time the feature appeared in their chosen conjoint concepts.
            Gap = revealed - stated. Positive means the feature performs better in choices than stated preference suggests.
          </p>
        </>
      )}
    </div>
  );
}
